import { sentenceCase } from 'change-case'
import { PlusIcon, TrashIcon } from 'lucide-react'
import React, { useState } from 'react'

import { AWXCardLimitsInterval } from '@ankor-io/common/types/awx/card'
import { currencies, formatThousandsOnly } from '@ankor-io/common/util/currencies'

import { Button } from '@/ui-components/Button'
import { Field, Fieldset, Label } from '@/ui-components/Fieldset'
import { Input } from '@/ui-components/Input'
import { Select } from '@/ui-components/Select'

import { type CardData } from './types'

type CardLimitsStepProps = {
  cardData: CardData
  setCardData: React.Dispatch<React.SetStateAction<CardData>>
  setAreLimitsValid: React.Dispatch<React.SetStateAction<boolean>>
}

export const CardLimitsStep = ({ cardData, setCardData, setAreLimitsValid }: CardLimitsStepProps) => {
  // Remove frequencyOptions, generate from enum
  const intervalValues = Object.values(AWXCardLimitsInterval)
  const [limits, setLimits] = useState<CardData['limits']>(cardData.limits)
  const [limitsInvalidMessage, setLimitsInvalidMessage] = useState<string | null>(null)

  const handleAmountChange = (index: number, value: string) => {
    // Remove any non-numeric characters except commas
    const cleaned = value.replace(/[^0-9,]/g, '')
    const numericValue = cleaned.replace(/,/g, '')
    let newLimits: CardData['limits']
    if (numericValue === '') {
      newLimits = limits.map((l, i) => (i === index ? { ...l, amount: undefined } : l))
      setLimits(newLimits)
    } else {
      const numValue = parseFloat(numericValue)
      if (isNaN(numValue)) {
        return
      }

      newLimits = limits.map((l, i) => (i === index ? { ...l, amount: numValue } : l))
      setLimits(newLimits)
    }

    setCardData((prev) => ({ ...prev, limits: newLimits }))
    checkLimitRankingValidity(newLimits)
  }

  const handleAmountBlur = (index: number, value: string) => {
    // Format with thousands separators on blur
    const formatted = formatThousandsOnly(value)
    if (formatted) {
      const numericValue = formatted.replace(/,/g, '')
      const numValue = parseFloat(numericValue)
      if (!isNaN(numValue)) {
        const newLimits = limits.map((l, i) => (i === index ? { ...l, amount: numValue } : l))
        setLimits(newLimits)
        setCardData((prev) => ({ ...prev, limits: newLimits }))
      }
    }
  }

  const handleIntervalChange = (index: number, value: string) => {
    const newLimits = limits.map((l, i) =>
      i === index ? { ...l, interval: value, intervalRank: intervalValues.findIndex((option) => option === value) } : l,
    )
    setLimits(newLimits)
    setCardData((prev) => ({ ...prev, limits: newLimits }))
    checkLimitRankingValidity(newLimits)
  }

  const handleAddLimit = () => {
    if (limits.length < 5) {
      const newLimits = [...limits, { amount: undefined, interval: undefined }]
      setLimits(newLimits)
      setCardData((prev) => ({ ...prev, limits: newLimits }))
    }
  }

  const handleRemoveLimit = (index: number) => {
    const newLimits = limits.filter((_, i) => i !== index)
    setLimits(newLimits)
    setCardData((prev) => ({ ...prev, limits: newLimits }))
  }

  const checkLimitRankingValidity = (cardLimits: CardData['limits']) => {
    if (cardLimits.length === 1) {
      return
    }

    for (let i = 0; i < cardLimits.length - 1; i++) {
      for (let j = i + 1; j < cardLimits.length; j++) {
        // left side interval is higher than right side interval and left side amount is less than or equal to right side amount
        // but it should be a higher amount if the interval is
        if (
          cardLimits[i].intervalRank! > cardLimits[j].intervalRank! &&
          cardLimits[i].amount! <= cardLimits[j].amount!
        ) {
          setAreLimitsValid(false)
          setLimitsInvalidMessage(
            `The amount for ${cardLimits[i].interval} is less than or equal to the amount for ${cardLimits[j].interval}.`,
          )
          return
        }

        // same thing but if the right side interval is higher than the left side interval and the right side amount is less than or equal to the left side amount
        if (
          cardLimits[i].intervalRank! < cardLimits[j].intervalRank! &&
          cardLimits[i].amount! >= cardLimits[j].amount!
        ) {
          setAreLimitsValid(false)
          setLimitsInvalidMessage(
            `The amount for ${cardLimits[i].interval} is greater than or equal to the amount for ${cardLimits[j].interval}.`,
          )
          return
        }

        setAreLimitsValid(true)
        setLimitsInvalidMessage(null)
      }
    }
  }

  return (
    <div className="space-y-4">
      <Fieldset className="space-y-4">
        <Field>
          <Label htmlFor="currency">Currency</Label>
          <Select
            id="currency"
            value={cardData.currency}
            onChange={(e) => setCardData((prev) => ({ ...prev, currency: e.target.value }))}
          >
            {!cardData.currency && <option value="">Select currency</option>}
            {currencies.map((currency) => (
              <option
                key={currency.code}
                value={currency.code}
              >
                {currency.code}
              </option>
            ))}
          </Select>
        </Field>
        <div className="flex gap-4 items-center">
          <Label className="flex-1">Amount</Label>
          <Label className="flex-1">Frequency</Label>
          {limits.length > 1 && (
            <Button
              plain
              className="!invisible size-0"
            >
              <TrashIcon
                data-slot="icon"
                className="size-0 !text-red-500 dark:!text-red-400"
              />
            </Button>
          )}
        </div>
        <div className="flex flex-col gap-4 items-center">
          {limits.map((limit, index) => (
            <div
              className="w-full flex gap-4 items-center"
              key={index}
            >
              <Field className="flex-1">
                <Input
                  id="amount"
                  type="text"
                  placeholder="Enter an amount"
                  value={limit.amount ? formatThousandsOnly(limit.amount.toString()) : ''}
                  onChange={(e) => handleAmountChange(index, e.target.value)}
                  onBlur={(e) => handleAmountBlur(index, e.target.value)}
                  required
                />
              </Field>
              <Field className="flex-1">
                <Select
                  id="frequency"
                  data-testid={`frequency-${index}`}
                  value={limit.interval}
                  onChange={(e) => handleIntervalChange(index, e.target.value)}
                >
                  {!limits[index].interval && <option value="">Select frequency</option>}
                  {intervalValues.map((option) => (
                    <option
                      key={option}
                      value={option}
                      disabled={limits.map((l) => l.interval).includes(option)}
                    >
                      {sentenceCase(option)}
                    </option>
                  ))}
                </Select>
              </Field>
              {limits.length > 1 && (
                <Button
                  plain
                  onClick={() => handleRemoveLimit(index)}
                >
                  <TrashIcon
                    data-slot="icon"
                    className="size-4 !text-red-500 dark:!text-red-400"
                  />
                </Button>
              )}
            </div>
          ))}
        </div>
        {limitsInvalidMessage && <p className="text-xs text-red-500 dark:text-red-400">{limitsInvalidMessage}</p>}
        {limits.length < 5 && (
          <Button
            color="primary"
            onClick={handleAddLimit}
          >
            <PlusIcon
              data-slot="icon"
              className="size-4"
            />
            Additional Limit
          </Button>
        )}
      </Fieldset>
    </div>
  )
}

export default CardLimitsStep
